/**
 * Per-device display gates.
 *
 * Pure decision logic for "should this notification surface as an OS toast,
 * silently land in the panel, or be dropped entirely?". The function takes a
 * snapshot of the user's per-device preferences (hidden apps, DND schedule,
 * one-shot snooze) and returns a tagged decision.
 *
 * NO side effects, NO platform globals (no DOM, no `chrome.*`, no `self`,
 * no IndexedDB). The extension's background SW and the web app's foreground
 * page + service worker each invoke `decideDisplay` with their own loaded
 * prefs and route the OS-toast / panel-write / badge calls accordingly.
 */
import type { ReflectoStorage } from "../storage-schema";

export type DisplayDecision = "show" | "silent" | "hide";

/**
 * Shared empty bypass-list reference used by `decideDisplay` when no DND
 * schedule is enabled. Hoisted to module scope so each call avoids a
 * fresh `[]` allocation; `Object.freeze` makes accidental mutation both
 * a type error and a runtime error.
 */
const EMPTY_BYPASS: readonly string[] = Object.freeze([]);

/**
 * Decide how (or whether) a decrypted notification should be displayed
 * on this device.
 *
 *  - `"hide"`   — drop entirely (app is in `hiddenApps`). The notification
 *                 should not even reach the panel.
 *  - `"silent"` — append to the panel, but skip toast / sound / badge bump.
 *                 DND schedule is active or a snooze window is in effect,
 *                 and the app is not in the bypass list.
 *  - `"show"`   — full notification UX (panel append + OS toast + badge).
 *
 * NOTE on the "hide" semantic: regular notification flows treat "hide" as
 * "drop entirely — no panel write." Conversation flows persist the message
 * BEFORE the gate (so multi-device read-state stays coherent), so a "hide"
 * there means "no toast/badge but the message lives in the conversation
 * thread." Document this in the call-site if you wire a new flow.
 *
 * NOTE on bypass: `bypassApps` applies ONLY when the schedule is enabled.
 * Snooze with no enabled schedule = no bypass list = silent for every app.
 * The DndEditor only surfaces the bypass picker while
 * `schedule?.enabled === true`, and this gate mirrors that contract — a
 * stale bypass list left over from a now-disabled schedule must not
 * silently leak through during a snooze.
 *
 * @param appPackage Android package name (e.g. `"com.whatsapp"`).
 * @param prefs Snapshot of relevant `ReflectoStorage` fields. Only the three
 *              gate-related keys are required; the caller may pass additional
 *              fields without effect.
 * @param now Injectable clock for testability. Defaults to `new Date()`.
 */
export function decideDisplay(
  appPackage: string,
  prefs: Pick<ReflectoStorage, "hiddenApps" | "dndSchedule" | "dndSnoozeUntil">,
  now: Date = new Date()
): DisplayDecision {
  if (prefs.hiddenApps?.includes(appPackage)) return "hide";

  const inSnooze = prefs.dndSnoozeUntil != null && prefs.dndSnoozeUntil > now.getTime();
  const inSchedule = prefs.dndSchedule?.enabled === true && isInDndWindow(prefs.dndSchedule, now);

  if (inSnooze || inSchedule) {
    // Bypass list is only honored when the schedule is enabled — see the
    // doc comment above. Disabled-schedule + snooze = silent for every
    // app, matching the "Snooze 1h" intent and the editor's UI contract
    // (the bypass picker is hidden while the schedule is disabled).
    const scheduledBypass: readonly string[] =
      prefs.dndSchedule?.enabled === true ? prefs.dndSchedule.bypassApps : EMPTY_BYPASS;
    if (!scheduledBypass.includes(appPackage)) {
      return "silent";
    }
  }
  return "show";
}

/**
 * True if `now` falls within the configured DND window. The window is
 * specified in minutes-from-midnight in the device's local timezone and
 * may cross midnight (e.g. 22:00–07:00 → start=1320, end=420).
 *
 * For non-wrapping windows the weekday check uses `Date#getDay()` on
 * `now` directly. For windows that wrap midnight (start > end) the
 * weekday that "owns" each side of midnight is split:
 *   - Evening portion (start..23:59) is owned by TODAY's weekday — the
 *     window starts then.
 *   - Early-morning portion (00:00..end) is owned by YESTERDAY's
 *     weekday — that's the day the user was awake for when the window
 *     started.
 *
 * Concretely: a Mon–Fri 22:00–07:00 schedule silences Saturday
 * 00:00–07:00 (yesterday=Fri ∈ weekdays) but does NOT silence Saturday
 * 22:00–Sunday 07:00 (Sat ∉ weekdays).
 */
function isInDndWindow(schedule: NonNullable<ReflectoStorage["dndSchedule"]>, now: Date): boolean {
  const minute = now.getHours() * 60 + now.getMinutes();
  const today = now.getDay();
  const yesterday = (today - 1 + 7) % 7;

  if (schedule.startMinute <= schedule.endMinute) {
    // Non-wrapping window — only today's weekday matters.
    // startMinute === endMinute: empty range, never matches (minute >= N && minute < N is always false).
    return (
      schedule.weekdays.includes(today) &&
      minute >= schedule.startMinute &&
      minute < schedule.endMinute
    );
  }

  // Wrapping window (e.g. 22:00–07:00):
  //   - Evening portion (start..23:59): owned by TODAY's weekday.
  //   - Early-morning portion (00:00..end): owned by YESTERDAY's weekday
  //     (because that's when the window started).
  if (minute >= schedule.startMinute) {
    return schedule.weekdays.includes(today);
  }
  if (minute < schedule.endMinute) {
    return schedule.weekdays.includes(yesterday);
  }
  return false;
}
